import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import UploadIcon from "@mui/icons-material/Upload";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { API_URL } from "./config";

const DISPLAY_FIELDS = [
  "StudentKey",
  "StudentID",
  "ApplicationID",
  "IsStudentActive",
  "CampusCode",
  "SubCampusCode",
  "DegreeCode",
  "BranchCode",
  "Section",
  "Semester",
];

const PAGE_SIZE_OPTIONS = [25, 50, 100, 250];

const UpdateStudentID = () => {
  const THEME_ORANGE = "#F07828";
  const THEME_ORANGE_2 = "#ed7f35ff";
  const BG = "#F2F2F2";
  const BORDER = "rgba(25,118,210,0.20)";
  const BORDER_HOVER = "rgba(25,118,210,0.45)";
  const BORDER_FOCUS = "rgba(240,120,40,0.80)";
  const ROW_ALT = "rgba(25,118,210,0.06)";
  const ROW_HOVER = "rgba(25,118,210,0.10)";

  const pageWrapSx = { p: 2, backgroundColor: BG };
  const rootPaperSx = {
    mx: "auto",
    borderRadius: 2,
    overflow: "hidden",
    border: "1px solid rgba(25,118,210,0.15)",
  };
  const headerSx = {
    px: 2,
    py: 1.5,
    background: `linear-gradient(90deg, ${THEME_ORANGE} 0%, ${THEME_ORANGE_2} 100%)`,
    color: "white",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 2,
    flexWrap: "wrap",
  };
  const bodySx = { p: 2, backgroundColor: BG };
  const surfaceSx = {
    borderRadius: 2,
    backgroundColor: "white",
    border: `1px solid ${BORDER}`,
  };
  const inputSx = {
    "& .MuiOutlinedInput-notchedOutline": { borderColor: BORDER },
    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: BORDER_HOVER },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: BORDER_FOCUS },
  };
  const autoSx = {
    minWidth: 200,
    "& .MuiOutlinedInput-root": {
      borderRadius: 2,
      "& .MuiOutlinedInput-notchedOutline": { borderColor: BORDER },
      "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: BORDER_HOVER },
      "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: BORDER_FOCUS },
    },
    "& .MuiChip-root": {
      borderRadius: 2,
      backgroundColor: "rgba(25,118,210,0.08)",
      border: "1px solid rgba(25,118,210,0.18)",
    },
  };
  const primaryBtnSx = {
    borderRadius: 2,
    textTransform: "none",
    fontWeight: 900,
    backgroundColor: THEME_ORANGE,
    "&:hover": { backgroundColor: THEME_ORANGE_2 },
  };
  const outlinedBtnSx = {
    borderRadius: 2,
    textTransform: "none",
    fontWeight: 900,
    borderColor: "rgba(25,118,210,0.35)",
    color: "rgba(25,118,210,0.95)",
    "&:hover": {
      borderColor: "rgba(25,118,210,0.55)",
      backgroundColor: "rgba(25,118,210,0.04)",
    },
  };
  const iconBtnSx = {
    borderRadius: 2,
    color: "rgba(25,118,210,0.95)",
    "&:hover": {
      backgroundColor: "rgba(240,120,40,0.10)",
      color: THEME_ORANGE,
    },
  };
  const theadRowSx = {
    backgroundColor: "rgba(25,118,210,0.95)",
    "& th": {
      color: "#fff",
      fontWeight: 900,
      borderBottom: "1px solid rgba(255,255,255,0.18)",
      whiteSpace: "nowrap",
    },
  };
  const zebraRowSx = (idx) => ({
    backgroundColor: idx % 2 === 0 ? ROW_ALT : "#FFFFFF",
    "&:hover": { backgroundColor: ROW_HOVER },
    "& td": { borderBottom: "1px solid rgba(25,118,210,0.10)" },
  });

  const [data, setData] = useState([]);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [loginCampusCode, setCampusCode] = useState("");
  const [loadingLookups, setLoadingLookups] = useState(false);
  const [loadingRows, setLoadingRows] = useState(false);
  const fileInputRef = useRef();
  const token = localStorage.getItem("auth");

  const [filters, setFilters] = useState({
    SubCampusCode: [],
    DegreeCode: [],
    BranchCode: [],
    SemesterCode: [],
    Section: [],
    IsStudentActive: ["Active"],
    HostelType: [],
    Entry: [],
    QualificationType: [],
    StudentID: [],
  });
  const [studentIdInput, setStudentIdInput] = useState("");

  const [options, setOptions] = useState({
    SubCampusCode: [],
    DegreeCode: [],
    BranchCode: [],
    SemesterCode: [],
    Section: [],
    HostelType: [],
    Entry: [],
    QualificationType: [],
  });

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 50,
    total: 0,
    totalPages: 0,
  });

  const [selectedKeys, setSelectedKeys] = useState([]);
  const [bulkSection, setBulkSection] = useState("");

  const [editOpen, setEditOpen] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);
  const [assigningSection, setAssigningSection] = useState(false);

  useEffect(() => {
    if (!token) return;
    try {
      const decoded = JSON.parse(atob(token.split(".")[1]));
      setCampusCode(decoded.campusCode || decoded.CampusCode || "");
    } catch {
      setError("Failed to decode authentication token");
    }
  }, [token]);

  const authHeader = useMemo(
    () => ({ headers: { Authorization: `Bearer ${token}` } }),
    [token]
  );

  useEffect(() => {
    if (!loginCampusCode) return;
    let cancelled = false;

    (async () => {
      try {
        setLoadingLookups(true);
        const { data: lookupData } = await axios.get(
          `${API_URL}/api/lookups/all`,
          {
            ...authHeader,
            params: { CampusCode: loginCampusCode },
          }
        );

        if (cancelled) return;

        setOptions({
          SubCampusCode: lookupData?.SubCampusCode || [],
          DegreeCode: lookupData?.DegreeCode || [],
          BranchCode: lookupData?.BranchCode || [],
          SemesterCode: (lookupData?.SemesterCode || []).map(String),
          Section: (lookupData?.Section || []).map(String),
          HostelType: (lookupData?.HostelType || []).map(String),
          Entry: (lookupData?.Entry || []).map(String),
          QualificationType: (lookupData?.QualificationType || []).map(String),
        });
      } catch (e) {
        console.error(e);
        setError("Failed to load dropdowns.");
      } finally {
        if (!cancelled) setLoadingLookups(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [loginCampusCode, authHeader]);

  const parseIDs = (txt) =>
    Array.from(
      new Set(
        (txt || "")
          .split(/[\s,]+/g)
          .map((s) => s.trim())
          .filter(Boolean)
      )
    );

  const joinParam = (arr) =>
    Array.isArray(arr) && arr.length ? arr.join(",") : undefined;

  const buildParams = (requestedPage = page, requestedPageSize = pageSize) => {
    const p = {
      page: requestedPage,
      pageSize: requestedPageSize,
    };

    const multiFilters = [
      ["SubCampusCode", filters.SubCampusCode],
      ["DegreeCode", filters.DegreeCode],
      ["BranchCode", filters.BranchCode],
      ["SemesterCode", filters.SemesterCode],
      ["Section", filters.Section],
      ["HostelType", filters.HostelType],
      ["Entry", filters.Entry],
      ["QualificationType", filters.QualificationType],
      ["StudentID", filters.StudentID],
    ];

    multiFilters.forEach(([key, values]) => {
      const joined = joinParam(values);
      if (joined) p[key] = joined;
    });

    if (filters.IsStudentActive.length) {
      p.IsStudentActive = filters.IsStudentActive
        .map((v) => (v === "Active" ? "1" : v === "Inactive" ? "0" : v))
        .join(",");
    }

    return p;
  };

  const fetchStudents = async (
    requestedPage = page,
    requestedPageSize = pageSize
  ) => {
    if (!loginCampusCode) {
      setError("Campus code not loaded yet.");
      return;
    }

    try {
      setError("");
      setLoadingRows(true);

      const res = await axios.get(`${API_URL}/api/UpdateStudentID`, {
        ...authHeader,
        params: buildParams(requestedPage, requestedPageSize),
      });

      const rows = Array.isArray(res.data) ? res.data : res.data?.rows || [];
      const pg = Array.isArray(res.data)
        ? {
            page: requestedPage,
            pageSize: requestedPageSize,
            total: rows.length,
            totalPages: 1,
          }
        : res.data?.pagination || {
            page: requestedPage,
            pageSize: requestedPageSize,
            total: 0,
            totalPages: 0,
          };

      setData(rows);
      setPagination(pg);
      setPage(pg.page || requestedPage);

      // Keep selections only for rows that still exist in the current result/filter set.
      const currentKeys = new Set(rows.map((r) => String(r.StudentKey)));
      setSelectedKeys((prev) =>
        prev.filter((key) => currentKeys.has(String(key)))
      );
    } catch (err) {
      console.error("Fetch students failed:", err);
      setError(
        err.response?.data?.error || "Failed to fetch student data"
      );
    } finally {
      setLoadingRows(false);
    }
  };

  const handleSearch = async () => {
    setSelectedKeys([]);
    setPage(1);
    await fetchStudents(1, pageSize);
  };

  const handlePageChange = async (_, nextPage) => {
    setSelectedKeys([]);
    setPage(nextPage);
    await fetchStudents(nextPage, pageSize);
  };

  const handlePageSizeChange = async (e) => {
    const nextSize = Number(e.target.value);
    setSelectedKeys([]);
    setPageSize(nextSize);
    setPage(1);
    await fetchStudents(1, nextSize);
  };

  const pageKeys = data.map((r) => String(r.StudentKey));
  const allPageSelected =
    pageKeys.length > 0 &&
    pageKeys.every((key) => selectedKeys.includes(key));
  const somePageSelected =
    pageKeys.some((key) => selectedKeys.includes(key)) && !allPageSelected;

  const toggleSelectAllPage = (checked) => {
    if (checked) {
      setSelectedKeys((prev) =>
        Array.from(new Set([...prev, ...pageKeys]))
      );
    } else {
      setSelectedKeys((prev) =>
        prev.filter((key) => !pageKeys.includes(key))
      );
    }
  };

  const toggleSelected = (studentKey) => {
    const key = String(studentKey);
    setSelectedKeys((prev) =>
      prev.includes(key)
        ? prev.filter((x) => x !== key)
        : [...prev, key]
    );
  };

  const openEditForm = (row) => {
    setEditRow({
      ...row,
      StudentID: row.StudentID ?? "",
      Section: row.Section ?? "",
    });
    setEditOpen(true);
  };

  const closeEditForm = () => {
    if (savingEdit) return;
    setEditOpen(false);
    setEditRow(null);
  };

  const handleEditField = (field, value) => {
    setEditRow((prev) => ({ ...prev, [field]: value }));
  };

  const saveIndividualUpdate = async () => {
    if (!editRow?.StudentKey) return;

    const studentID = String(editRow.StudentID || "").trim();
    if (!studentID) {
      setError("StudentID is required.");
      return;
    }

    try {
      setSavingEdit(true);
      setError("");

      await axios.put(
        `${API_URL}/api/UpdateStudentID/${editRow.StudentKey}`,
        {
          StudentID: studentID,
          Section: editRow.Section === "" ? null : editRow.Section,
        },
        authHeader
      );

      setSuccessMessage("Student updated successfully.");
      setEditOpen(false);
      setEditRow(null);
      await fetchStudents(page, pageSize);
    } catch (err) {
      setError(
        err.response?.data?.error ||
          "Failed to update student information"
      );
    } finally {
      setSavingEdit(false);
    }
  };

  const assignSectionToSelected = async () => {
    if (!selectedKeys.length) {
      setError("Select at least one student.");
      return;
    }

    if (!bulkSection) {
      setError("Choose a section to assign.");
      return;
    }

    try {
      setAssigningSection(true);
      setError("");

      const res = await axios.put(
        `${API_URL}/api/UpdateStudentID/assign-section`,
        {
          StudentKeys: selectedKeys.map(Number),
          Section: bulkSection,
        },
        authHeader
      );

      setSuccessMessage(
        res.data?.message ||
          `Section assigned to ${selectedKeys.length} student(s).`
      );
      setSelectedKeys([]);
      await fetchStudents(page, pageSize);
    } catch (err) {
      setError(
        err.response?.data?.error || "Failed to assign section"
      );
    } finally {
      setAssigningSection(false);
    }
  };

  const downloadSampleExcel = () => {
    const headers = ["StudentKey", "StudentID", "Section"];
    const sampleRows = [
      {
        StudentKey: 32109,
        StudentID: "226Q1A4203",
        Section: "A",
      },
      {
        StudentKey: 32110,
        StudentID: "226Q1A4204",
        Section: "",
      },
    ];

    const ws = XLSX.utils.json_to_sheet(sampleRows, { header: headers });
    ws["!cols"] = headers.map((h) => ({
      wch: Math.max(16, h.length + 2),
    }));

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "UpdateStudentID");

    const instructions = [
      ["Field", "Notes"],
      [
        "StudentKey",
        "Required. Primary key used to find the student row.",
      ],
      [
        "StudentID",
        "Optional. If supplied, StudentID is updated. Leave blank to keep the current StudentID.",
      ],
      [
        "Section",
        "Optional. Must match an active value in the sections table. Leave blank to keep the current Section.",
      ],
    ];
    const ws2 = XLSX.utils.aoa_to_sheet(instructions);
    ws2["!cols"] = [{ wch: 20 }, { wch: 90 }];
    XLSX.utils.book_append_sheet(wb, ws2, "Instructions");

    const out = XLSX.write(wb, {
      bookType: "xlsx",
      type: "array",
    });
    const blob = new Blob([out], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    saveAs(
      blob,
      `UpdateStudentID_Section_Sample_${loginCampusCode}.xlsx`
    );
  };

  const handleBulkClick = () => fileInputRef.current?.click();

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const form = new FormData();
    form.append("excelFile", file);

    try {
      setError("");
      await axios.post(
        `${API_URL}/api/UploadUpdateStudentID`,
        form,
        {
          ...authHeader,
          headers: {
            ...authHeader.headers,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      setSuccessMessage("Bulk upload completed successfully.");
      setSelectedKeys([]);
      await fetchStudents(page, pageSize);
    } catch (err) {
      setError(
        err.response?.data?.error ||
          err.response?.data?.details ||
          "Bulk upload failed"
      );
    } finally {
      e.target.value = "";
    }
  };

  const activeLabel = (value) =>
    value === 1 || value === "1" || value === true
      ? "Active"
      : "Inactive";

  return (
    <Box sx={pageWrapSx}>
      <Paper elevation={5} sx={rootPaperSx}>
        <Box sx={headerSx}>
          <Box>
            <Typography
              sx={{
                fontSize: "1.1rem",
                fontWeight: 900,
                lineHeight: 1.2,
              }}
            >
              Update Student ID & Section
            </Typography>
            <Typography sx={{ opacity: 0.9, fontSize: "0.85rem" }}>
              Filter students, update an individual student, or assign a section to multiple students
            </Typography>
          </Box>

          <Stack direction="row" spacing={1} alignItems="center">
            <Button
              variant="outlined"
              onClick={downloadSampleExcel}
              sx={{
                ...outlinedBtnSx,
                color: "#fff",
                borderColor: "rgba(255,255,255,0.75)",
                "&:hover": {
                  borderColor: "#fff",
                  backgroundColor: "rgba(255,255,255,0.10)",
                },
              }}
            >
              Download Sample
            </Button>

            <Button
              variant="contained"
              startIcon={<UploadIcon />}
              onClick={handleBulkClick}
              sx={{
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 900,
                backgroundColor: "#fff",
                color: THEME_ORANGE,
                "&:hover": { backgroundColor: "rgba(255,255,255,0.90)" },
              }}
            >
              Bulk Upload
            </Button>

            <input
              type="file"
              accept=".xlsx,.xls"
              ref={fileInputRef}
              style={{ display: "none" }}
              onChange={handleFile}
            />
          </Stack>
        </Box>

        <Box sx={bodySx}>
          {!!error && (
            <Alert
              severity="error"
              onClose={() => setError("")}
              sx={{ mb: 2, borderRadius: 2 }}
            >
              {error}
            </Alert>
          )}

          {!!successMessage && (
            <Snackbar
              open
              autoHideDuration={3000}
              onClose={() => setSuccessMessage("")}
            >
              <Alert
                severity="success"
                onClose={() => setSuccessMessage("")}
                sx={{ borderRadius: 2 }}
              >
                {successMessage}
              </Alert>
            </Snackbar>
          )}

          <Box
            sx={{
              ...surfaceSx,
              p: 1.25,
              mb: 2,
            }}
          >
            {/*
              FILTER GRID WIDTHS
              ------------------
              Change the Grid item xs/sm/md/lg values below whenever you want
              to control exactly how much horizontal space each filter occupies.

              MUI Grid uses 12 columns per row.

              Example:
                md={2} = 2/12 width
                md={3} = 3/12 width
                md={4} = 4/12 width
                md={6} = 6/12 width
            */}

            {/* ===== Filter Row 1 ===== */}
            <Grid container spacing={1} alignItems="center">
              <Grid item xs={12} sm={6} md={2} lg={2}>
                <Autocomplete
                  multiple
                  options={options.SubCampusCode}
                  value={filters.SubCampusCode}
                  onChange={(_, value) =>
                    setFilters((p) => ({ ...p, SubCampusCode: value }))
                  }
                  loading={loadingLookups}
                  sx={{ ...autoSx, minWidth: 0, width: "100%" }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="SubCampusCode(s)"
                      size="small"
                      sx={inputSx}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={2} lg={2}>
                <Autocomplete
                  multiple
                  options={options.DegreeCode}
                  value={filters.DegreeCode}
                  onChange={(_, value) =>
                    setFilters((p) => ({ ...p, DegreeCode: value }))
                  }
                  loading={loadingLookups}
                  sx={{ ...autoSx, minWidth: 0, width: "100%" }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="DegreeCode(s)"
                      size="small"
                      sx={inputSx}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={2} lg={2}>
                <Autocomplete
                  multiple
                  options={options.BranchCode}
                  value={filters.BranchCode}
                  onChange={(_, value) =>
                    setFilters((p) => ({ ...p, BranchCode: value }))
                  }
                  loading={loadingLookups}
                  sx={{ ...autoSx, minWidth: 0, width: "100%" }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="BranchCode(s)"
                      size="small"
                      sx={inputSx}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={2} lg={1.5}>
                <Autocomplete
                  multiple
                  options={options.SemesterCode}
                  value={filters.SemesterCode}
                  onChange={(_, value) =>
                    setFilters((p) => ({ ...p, SemesterCode: value }))
                  }
                  loading={loadingLookups}
                  sx={{ ...autoSx, minWidth: 0, width: "100%" }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="SemesterCode(s)"
                      size="small"
                      sx={inputSx}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={2} lg={1.5}>
                <Autocomplete
                  multiple
                  options={["__BLANK__", ...options.Section]}
                  value={filters.Section}
                  onChange={(_, value) =>
                    setFilters((p) => ({ ...p, Section: value }))
                  }
                  getOptionLabel={(option) =>
                    option === "__BLANK__" ? "Blank" : String(option)
                  }
                  loading={loadingLookups}
                  sx={{ ...autoSx, minWidth: 0, width: "100%" }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Section(s)"
                      size="small"
                      sx={inputSx}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={2} lg={1.5}>
                <Autocomplete
                  multiple
                  options={["Active", "Inactive"]}
                  value={filters.IsStudentActive}
                  onChange={(_, value) =>
                    setFilters((p) => ({ ...p, IsStudentActive: value }))
                  }
                  sx={{ ...autoSx, minWidth: 0, width: "100%" }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Is Active"
                      size="small"
                      sx={inputSx}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={2} lg={1.5}>
                <Autocomplete
                  multiple
                  options={options.HostelType}
                  value={filters.HostelType}
                  onChange={(_, value) =>
                    setFilters((p) => ({ ...p, HostelType: value }))
                  }
                  loading={loadingLookups}
                  sx={{ ...autoSx, minWidth: 0, width: "100%" }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="HostelType(s)"
                      size="small"
                      sx={inputSx}
                    />
                  )}
                />
              </Grid>
            </Grid>

            {/* ===== Filter Row 2 ===== */}
            <Grid container spacing={1} alignItems="center" sx={{ mt: 0.25 }}>
              <Grid item xs={12} sm={6} md={2} lg={2}>
                <Autocomplete
                  multiple
                  options={options.Entry}
                  value={filters.Entry}
                  onChange={(_, value) =>
                    setFilters((p) => ({ ...p, Entry: value }))
                  }
                  loading={loadingLookups}
                  sx={{ ...autoSx, minWidth: 0, width: "100%" }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Entry(s)"
                      size="small"
                      sx={inputSx}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={2} lg={2}>
                <Autocomplete
                  multiple
                  options={options.QualificationType}
                  value={filters.QualificationType}
                  onChange={(_, value) =>
                    setFilters((p) => ({ ...p, QualificationType: value }))
                  }
                  loading={loadingLookups}
                  sx={{ ...autoSx, minWidth: 0, width: "100%" }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="QualificationType(s)"
                      size="small"
                      sx={inputSx}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={8} md={7} lg={7}>
                <TextField
                  fullWidth
                  size="small"
                  label="Student IDs"
                  placeholder="Comma / space / newline"
                  value={studentIdInput}
                  sx={inputSx}
                  onChange={(e) => {
                    const txt = e.target.value;
                    setStudentIdInput(txt);
                    setFilters((p) => ({
                      ...p,
                      StudentID: parseIDs(txt),
                    }));
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={4} md={1} lg={1}>
                <Button
                  fullWidth
                  variant="contained"
                  sx={{ ...primaryBtnSx, height: 40 }}
                  onClick={handleSearch}
                  disabled={loadingRows}
                >
                  {loadingRows ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    "Search"
                  )}
                </Button>
              </Grid>
            </Grid>
          </Box>

          <Box sx={{ ...surfaceSx, p: 1.25, mb: 2 }}>
            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={1}
              alignItems={{ xs: "stretch", md: "center" }}
            >
              <Typography sx={{ fontWeight: 800, minWidth: 150 }}>
                Selected: {selectedKeys.length}
              </Typography>

              <FormControl
                size="small"
                sx={{ minWidth: 220 }}
                disabled={!selectedKeys.length}
              >
                <InputLabel>Assign Section</InputLabel>
                <Select
                  label="Assign Section"
                  value={bulkSection}
                  onChange={(e) => setBulkSection(e.target.value)}
                >
                  {options.Section.map((section) => (
                    <MenuItem key={section} value={section}>
                      {section}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Button
                variant="contained"
                sx={primaryBtnSx}
                disabled={
                  !selectedKeys.length ||
                  !bulkSection ||
                  assigningSection
                }
                onClick={assignSectionToSelected}
              >
                {assigningSection
                  ? "Assigning..."
                  : "Assign Section to Selected"}
              </Button>

              {!!selectedKeys.length && (
                <Button
                  variant="outlined"
                  sx={outlinedBtnSx}
                  onClick={() => setSelectedKeys([])}
                >
                  Clear Selection
                </Button>
              )}
            </Stack>
          </Box>

          <Paper
            elevation={0}
            sx={{
              ...surfaceSx,
              overflow: "hidden",
            }}
          >
            <Box sx={{ overflowX: "auto" }}>
              <TableContainer component="div">
                <Table size="small" sx={{ minWidth: 1100 }}>
                  <TableHead>
                    <TableRow sx={theadRowSx}>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={allPageSelected}
                          indeterminate={somePageSelected}
                          onChange={(e) =>
                            toggleSelectAllPage(e.target.checked)
                          }
                          sx={{
                            color: "white",
                            "&.Mui-checked": { color: "white" },
                            "&.MuiCheckbox-indeterminate": {
                              color: "white",
                            },
                          }}
                        />
                      </TableCell>
                      <TableCell>#</TableCell>
                      {DISPLAY_FIELDS.map((field) => (
                        <TableCell key={field}>{field}</TableCell>
                      ))}
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {data.length === 0 ? (
                      <TableRow>
                        <TableCell
                          colSpan={DISPLAY_FIELDS.length + 3}
                          align="center"
                          sx={{ py: 4 }}
                        >
                          {loadingRows
                            ? "Loading..."
                            : "No students found."}
                        </TableCell>
                      </TableRow>
                    ) : (
                      data.map((row, idx) => {
                        const key = String(row.StudentKey);
                        return (
                          <TableRow
                            key={row.StudentKey}
                            sx={zebraRowSx(idx)}
                          >
                            <TableCell padding="checkbox">
                              <Checkbox
                                checked={selectedKeys.includes(key)}
                                onChange={() =>
                                  toggleSelected(row.StudentKey)
                                }
                              />
                            </TableCell>

                            <TableCell>
                              {(pagination.page - 1) *
                                pagination.pageSize +
                                idx +
                                1}
                            </TableCell>

                            {DISPLAY_FIELDS.map((field) => (
                              <TableCell key={field}>
                                {field === "IsStudentActive"
                                  ? activeLabel(row[field])
                                  : field === "Section"
                                  ? row[field] || "—"
                                  : row[field]}
                              </TableCell>
                            ))}

                            <TableCell>
                              <IconButton
                                sx={iconBtnSx}
                                onClick={() => openEditForm(row)}
                                title="Edit student"
                              >
                                <EditIcon />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>

            <Box
              sx={{
                p: 1.5,
                display: "flex",
                gap: 2,
                alignItems: "center",
                justifyContent: "space-between",
                flexWrap: "wrap",
                borderTop: `1px solid ${BORDER}`,
              }}
            >
              <Typography variant="body2">
                Total students: <b>{pagination.total}</b>
              </Typography>

              <Stack direction="row" spacing={2} alignItems="center">
                <FormControl size="small" sx={{ minWidth: 110 }}>
                  <InputLabel>Page size</InputLabel>
                  <Select
                    label="Page size"
                    value={pageSize}
                    onChange={handlePageSizeChange}
                  >
                    {PAGE_SIZE_OPTIONS.map((size) => (
                      <MenuItem key={size} value={size}>
                        {size}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <Pagination
                  page={pagination.page || 1}
                  count={Math.max(pagination.totalPages || 1, 1)}
                  onChange={handlePageChange}
                  color="primary"
                  shape="rounded"
                  showFirstButton
                  showLastButton
                />
              </Stack>
            </Box>
          </Paper>
        </Box>
      </Paper>

      <Dialog
        open={editOpen}
        onClose={closeEditForm}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Update Student</DialogTitle>

        <DialogContent dividers>
          {editRow && (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "1fr 1fr",
                },
                gap: 2,
                pt: 1,
              }}
            >
              <TextField
                label="StudentKey"
                value={editRow.StudentKey ?? ""}
                disabled
                size="small"
              />
              <TextField
                label="ApplicationID"
                value={editRow.ApplicationID ?? ""}
                disabled
                size="small"
              />
              <TextField
                label="CampusCode"
                value={editRow.CampusCode ?? ""}
                disabled
                size="small"
              />
              <TextField
                label="SubCampusCode"
                value={editRow.SubCampusCode ?? ""}
                disabled
                size="small"
              />
              <TextField
                label="DegreeCode"
                value={editRow.DegreeCode ?? ""}
                disabled
                size="small"
              />
              <TextField
                label="BranchCode"
                value={editRow.BranchCode ?? ""}
                disabled
                size="small"
              />
              <TextField
                label="Semester"
                value={editRow.Semester ?? ""}
                disabled
                size="small"
              />
              <TextField
                label="IsStudentActive"
                value={activeLabel(editRow.IsStudentActive)}
                disabled
                size="small"
              />

              <TextField
                label="StudentID"
                value={editRow.StudentID ?? ""}
                onChange={(e) =>
                  handleEditField("StudentID", e.target.value)
                }
                required
                size="small"
                sx={inputSx}
              />

              <FormControl size="small">
                <InputLabel>Section</InputLabel>
                <Select
                  label="Section"
                  value={editRow.Section ?? ""}
                  onChange={(e) =>
                    handleEditField("Section", e.target.value)
                  }
                >
                  <MenuItem value="">
                    <em>Blank / No section</em>
                  </MenuItem>
                  {options.Section.map((section) => (
                    <MenuItem key={section} value={section}>
                      {section}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          )}
        </DialogContent>

        <DialogActions>
          <Button
            onClick={closeEditForm}
            disabled={savingEdit}
            sx={outlinedBtnSx}
          >
            Cancel
          </Button>
          <Button
            onClick={saveIndividualUpdate}
            variant="contained"
            disabled={savingEdit}
            sx={primaryBtnSx}
          >
            {savingEdit ? "Saving..." : "Save Changes"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UpdateStudentID;
